/**
 * The app's control vocabulary, in one place.
 *
 * Every page used to declare its own `btn` / `field` / `eyebrow` / `h1`, and
 * because each conversion commit re-typed them they drifted: control heights
 * ran 30px on the lab screen, 34px in the app header, 36px in the docs reader
 * and 38px on Instructor/Monitor/Tools, with 4px and 8px corners mixed in. The
 * values below are the lab screen's, which is the surface the design was tuned
 * against — unifying the numbers without removing the duplication would only
 * have reset the clock on the same drift.
 *
 * Anything genuinely local to one page (a topology legend, a slide stage) still
 * belongs in that page. This is for the controls that appear everywhere.
 */

/** 30px tall on an 8px radius — the one size for every interactive control. */
export const CONTROL = "h-[30px] rounded-lg";

const btnBase = `${CONTROL} inline-flex shrink-0 items-center justify-center gap-1.5 border px-3 text-[11px] font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50`;

// Every variant carries a 1px border, including the filled one. Without it the
// filled button is 2px shorter than the outlined buttons beside it, which is
// half of why the lab's action row looked uneven before v.106.
/** The one filled button on a surface — the primary action, at most once. */
export const btnPrimary = `${btnBase} border-transparent bg-accent text-accent-on hover:bg-accent-hover`;
/** Default button. Quiet enough not to compete with the primary. */
export const btnQuiet = `${btnBase} border-line-soft bg-pane-left text-ink-muted hover:bg-line hover:text-ink-strong`;
/** Accent-lettered but neutral-filled: reads as important without rivalling primary. */
export const btnAccent = `${btnBase} border-accent/40 bg-line text-accent hover:bg-line-soft`;
/** Destructive. */
export const btnDanger = `${btnBase} border-danger/70 bg-transparent text-danger hover:bg-danger/10`;

/** Icon-only button, square on the same scale. */
export const iconButton = "inline-flex size-[30px] items-center justify-center rounded-lg text-ink-muted transition-colors hover:bg-pane-right hover:text-accent";
/** Pane rail toggle — same control as the lab session list collapse button. */
export const paneIconButton = "inline-flex size-[30px] items-center justify-center rounded-lg text-ink-muted transition-colors hover:bg-pane-right hover:text-accent";

/** Text input / select. */
export const field = `${CONTROL} min-w-0 border border-line-soft bg-pane-right px-3 text-[11px] text-ink placeholder:text-ink-faint outline-none transition-colors focus:border-accent focus:ring-1 focus:ring-accent/70`;

/** The bar every page opens with. Fixed height so bars line up across a split. */
export const pageBar = "flex h-[53px] shrink-0 items-center justify-between gap-2 border-b border-line bg-editor-header px-4";

// Underline tabs, self-stretched so the indicator sits on the bar's bottom
// border. `border-b-2` also sets the colour of the other three borders, which is
// why the base reset in styles.css has to zero their widths — without it the
// active tab draws a full rectangle instead of an underline.
export const tab = "inline-flex select-none items-center gap-2 self-stretch border-b-2 text-[12px] font-medium transition-colors";
export const tabOn = `${tab} border-accent text-accent`;
export const tabOff = `${tab} border-transparent text-ink-muted hover:text-ink`;

// Segmented control — a bordered group with flush options: TH/EN, the docs
// Reader/Slide/Edit switch, the topology layer picker. All three existed with
// different heights and two different active treatments (solid accent vs a 15%
// tint); solid wins because it is what the header switch already used.
export const segmentGroup = "inline-flex items-center rounded-lg border border-line-soft bg-pane-right p-[2px]";
const segment = "h-[30px] rounded-md px-2.5 text-[11px] font-semibold transition-colors disabled:cursor-not-allowed";
export const segmentOn = `${segment} bg-accent text-accent-on`;
export const segmentOff = `${segment} text-ink-muted hover:text-ink`;

export const eyebrow = "font-mono text-[0.68rem] font-semibold tracking-[0.08em] text-accent";
export const h1 = "my-0.5 text-[clamp(1.25rem,2vw,1.625rem)] font-semibold leading-[1.42] text-ink-strong";
export const card = "rounded-xl border border-line bg-pane-left";
export const badge = "inline-flex min-h-[24px] items-center rounded-full px-2 py-0.5 text-[0.76rem]";
